/**
 * 简化的菜单数据管理Hook
 * 使用全局变量防止重复加载
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { menuService } from '../services/menuService';
import { MenuItem } from '../types/menu';
import { processSidebarMenuData } from '../utils/simpleMenuProcessor';
import { eventBus } from '../utils/eventBus';

// 全局状态，防止重复加载
// 使用Map存储不同用户的菜单数据，避免用户切换时使用错误的缓存
// 对于网站端（未登录），使用 'website' 作为key
let globalMenuDataMap: Map<number | string, MenuItem[]> = new Map();
let globalIsLoading = false;
let globalIsLoadedMap: Map<number | string, boolean> = new Map();
// 使用 Promise 队列，确保同一时间只有一个请求在执行
let loadingPromise: Promise<void> | null = null;
let loadingCacheKey: number | string | null = null;

// 将全局变量暴露到 window 对象，供其他组件使用（避免重复调用接口）
if (typeof window !== 'undefined') {
  (window as any).__globalMenuDataMap__ = globalMenuDataMap;
  (window as any).__globalIsLoadedMap__ = globalIsLoadedMap;
  (window as any).__globalIsLoading__ = globalIsLoading;
  (window as any).__loadingPromise__ = loadingPromise;
}
// 用于防止多个组件同时创建 setTimeout
let pendingLoadTimer: NodeJS.Timeout | null = null;
// 用于标记是否已经有组件在等待加载（防止多个组件同时创建定时器）
let isWaitingForLoad = false;
// 用于标记是否已经初始化过（确保整个应用只初始化一次）
// 使用原子操作标志，确保只有一个组件能通过检查
let hasInitialized = false;
// 用于标记是否正在初始化（防止多个组件同时进入初始化流程）
let isInitializing = false;

// localStorage 缓存键名
const MENU_CACHE_KEY = 'menu_data_cache';
const MENU_CACHE_VERSION = '2.0.0'; // 更新版本号，强制清除旧缓存

// 从 localStorage 加载缓存的菜单数据
const loadMenuCache = (): MenuItem[] => {
  try {
    const cached = localStorage.getItem(MENU_CACHE_KEY);
    if (cached) {
      const { data, version } = JSON.parse(cached);
      if (version === MENU_CACHE_VERSION && Array.isArray(data) && data.length > 0) {
        return data;
      }
    }
  } catch (error) {
    // 缓存读取失败，忽略
  }
  return [];
};

// 保存菜单数据到 localStorage
const saveMenuCache = (data: MenuItem[]) => {
  try {
    localStorage.setItem(MENU_CACHE_KEY, JSON.stringify({
      data,
      version: MENU_CACHE_VERSION,
      timestamp: Date.now()
    }));
  } catch (error) {
    // 缓存保存失败，忽略
  }
};

// 初始化：不再从缓存加载菜单数据，因为菜单是用户相关的
// 每次登录后都应该重新获取当前用户的授权菜单
// const cachedMenuData = loadMenuCache();
// if (cachedMenuData.length > 0 && !globalIsLoaded) {
//   globalMenuData = cachedMenuData;
// }

export const useMenuDataSimple = () => {
  // 在初始化时立即从缓存读取数据，避免多个组件同时触发加载
  const getInitialMenuData = (): MenuItem[] => {
    let currentUserId: number | null = null;
    try {
      const userInfoStr = localStorage.getItem('user_info');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        currentUserId = userInfo?.id || null;
      }
    } catch (e) {
      // 忽略解析错误
    }
    
    const isWebsitePath = window.location.pathname.startsWith('/show');
    let cacheKey: number | string | null = currentUserId;
    if (!cacheKey && isWebsitePath) {
      cacheKey = 'website';
    }
    
    if (cacheKey) {
      const isLoaded = globalIsLoadedMap.get(cacheKey) || false;
      const cachedData = globalMenuDataMap.get(cacheKey);
      
      if (isLoaded && cachedData && cachedData.length > 0) {
        return cachedData;
      }
    }
    
    return [];
  };
  
  // 使用初始化函数从缓存读取数据
  const [menuData, setMenuData] = useState<MenuItem[]>(getInitialMenuData);
  const [loading, setLoading] = useState(false);

  const loadMenuData = useCallback(async (forceReload = false) => {
    // 如果正在加载且不是强制重新加载，等待现有请求完成（防止重复调用）
    // 优先检查 loadingPromise，因为它是最可靠的标志
    if (!forceReload && (loadingPromise || globalIsLoading || isInitializing)) {
      // 如果 loadingPromise 存在，等待它
      if (loadingPromise) {
        try {
          await loadingPromise;
          // 请求完成后，使用缓存数据
          let currentUserId: number | null = null;
          try {
            const userInfoStr = localStorage.getItem('user_info');
            if (userInfoStr) {
              const userInfo = JSON.parse(userInfoStr);
              currentUserId = userInfo?.id || null;
            }
          } catch (e) {
            // 忽略解析错误
          }
          const isWebsitePath = window.location.pathname.startsWith('/show');
          let cacheKey: number | string | null = currentUserId;
          if (!cacheKey && isWebsitePath) {
            cacheKey = 'website';
          }
          if (cacheKey) {
            const cachedData = globalMenuDataMap.get(cacheKey);
            if (cachedData && cachedData.length > 0) {
              setMenuData(cachedData);
            }
          }
        } catch (error) {
          console.error('[useMenuDataSimple] 等待请求完成失败:', error);
        }
      } else {
        // 如果 loadingPromise 不存在但标志显示正在加载，等待一小段时间让 loadingPromise 被创建
        let retries = 0;
        while (!loadingPromise && (globalIsLoading || isInitializing) && retries < 10) {
          await new Promise(resolve => setTimeout(resolve, 50));
          retries++;
        }
        if (loadingPromise) {
          try {
            await loadingPromise;
            let currentUserId: number | null = null;
            try {
              const userInfoStr = localStorage.getItem('user_info');
              if (userInfoStr) {
                const userInfo = JSON.parse(userInfoStr);
                currentUserId = userInfo?.id || null;
              }
            } catch (e) {
              // 忽略解析错误
            }
            const isWebsitePath = window.location.pathname.startsWith('/show');
            let cacheKey: number | string | null = currentUserId;
            if (!cacheKey && isWebsitePath) {
              cacheKey = 'website';
            }
            if (cacheKey) {
              const cachedData = globalMenuDataMap.get(cacheKey);
              if (cachedData && cachedData.length > 0) {
                setMenuData(cachedData);
              }
            }
          } catch (error) {
            // 忽略错误
          }
        }
      }
      return;
    }
    
    // 获取当前用户ID
    let currentUserId: number | null = null;
    try {
      const userInfoStr = localStorage.getItem('user_info');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        currentUserId = userInfo?.id || null;
      }
    } catch (e) {
      // 忽略解析错误
    }

    // 判断是否是网站端（/show 路径）
    const isWebsitePath = window.location.pathname.startsWith('/show');
    
    // 确定缓存key：如果是网站端且未登录，使用 'website'；否则使用用户ID
    // 如果既没有用户ID也不是网站端，无法加载菜单
    let cacheKey: number | string | null = currentUserId;
    if (!cacheKey && isWebsitePath) {
      cacheKey = 'website';
    }
    
    if (!cacheKey) {
      setMenuData([]);
      return;
    }

    // 如果正在加载相同缓存key的请求，等待现有请求完成（非强制重新加载时）
    if (!forceReload && loadingPromise && loadingCacheKey === cacheKey) {
      // 等待现有请求完成
      try {
        await loadingPromise;
        // 请求完成后，使用缓存数据
        const cachedData = globalMenuDataMap.get(cacheKey);
        if (cachedData && cachedData.length > 0) {
          setMenuData(cachedData);
        }
      } catch (error) {
        // 如果请求失败，继续执行新的请求
        console.error('[useMenuDataSimple] 等待菜单加载失败:', error);
      }
      return;
    }

    // 如果正在加载（即使不是同一个cacheKey），也等待完成（非强制重新加载时）
    if (!forceReload && (globalIsLoading || loadingPromise)) {
      // 等待现有请求完成
      if (loadingPromise) {
        try {
          await loadingPromise;
          // 请求完成后，检查是否有缓存数据
          const cachedData = globalMenuDataMap.get(cacheKey);
          if (cachedData && cachedData.length > 0) {
            setMenuData(cachedData);
          }
        } catch (error) {
          // 忽略错误
        }
      }
      return;
    }

    // 检查是否有缓存数据（非强制重新加载时）
    if (!forceReload) {
      const isLoaded = globalIsLoadedMap.get(cacheKey) || false;
      const cachedData = globalMenuDataMap.get(cacheKey);
      
      if (isLoaded && cachedData && cachedData.length > 0) {
        // 使用当前用户或网站端的缓存
        setMenuData(cachedData);
        return;
      }
    }

    // 再次检查，防止在获取 cacheKey 的过程中有其他请求已经开始
    // 这是一个双重检查锁定模式
    if (!forceReload && (loadingPromise || globalIsLoading || isInitializing)) {
      if (loadingPromise) {
        try {
          await loadingPromise;
          const cachedData = globalMenuDataMap.get(cacheKey);
          if (cachedData && cachedData.length > 0) {
            setMenuData(cachedData);
          }
        } catch (error) {
          console.error('[useMenuDataSimple] 双重检查等待失败:', error);
        }
      }
      return;
    }

    // 菜单权限是用户相关的，每次登录都应该重新加载
    // 使用用户ID或'website'作为缓存的key，确保不同用户使用不同的缓存
    if (forceReload) {
      // 清除当前用户或网站端的缓存
      globalIsLoadedMap.delete(cacheKey);
      globalMenuDataMap.delete(cacheKey);
      // 清除localStorage缓存
      try {
        localStorage.removeItem(MENU_CACHE_KEY);
      } catch (e) {
        // 忽略清除缓存失败
      }
      // 清除正在加载的 Promise（如果存在）
      loadingPromise = null;
      loadingCacheKey = null;
      isInitializing = false;
      globalIsLoading = false;
    }

    // 创建新的 Promise，用于其他组件等待
    // 先同步设置所有标志，确保后续调用能立即检测到
    // 使用原子操作，确保标志设置的顺序
    // 注意：必须在创建 loadingPromise 之前设置，否则其他组件可能检测不到
    isInitializing = true;
    globalIsLoading = true;
    loadingCacheKey = cacheKey;
    setLoading(true);
    
    // 最后一次检查：在创建 Promise 之前，再次确认没有其他请求
    // 这是三重检查锁定模式，确保绝对不会有并发请求
    if (!forceReload && loadingPromise) {
      // 重置标志，因为已经有请求在进行
      isInitializing = false;
      globalIsLoading = false;
      loadingCacheKey = null;
      setLoading(false);
      
      try {
        await loadingPromise;
        const cachedData = globalMenuDataMap.get(cacheKey);
        if (cachedData && cachedData.length > 0) {
          setMenuData(cachedData);
        }
      } catch (error) {
        console.error('[useMenuDataSimple] 三重检查等待失败:', error);
      }
      return;
    }
    
    // 创建新的 Promise，用于其他组件等待
    // 立即创建并设置 loadingPromise，确保其他组件能立即检测到
    // 这样可以避免在设置标志和创建 Promise 之间的时间窗口
    loadingPromise = (async () => {
      // 更新 window 对象上的引用
      if (typeof window !== 'undefined') {
        (window as any).__globalIsLoading__ = globalIsLoading;
        (window as any).__loadingPromise__ = loadingPromise;
      }
    try {
      // 使用getUserMenus获取用户授权的菜单（而不是所有菜单）
      // 对于网站端，这个接口现在返回所有菜单（后端已配置为公开）
      const { permissionService } = await import('../services/permissionService');
      const response = await permissionService.getUserMenus();
      
      if (response.code === 200) {
        const menus = response.data || [];
        
        // 将后端返回的Map格式转换为MenuItem格式
        const menuItems: MenuItem[] = menus.map((menu: any) => ({
          id: menu.id,
          menuName: menu.menuName,
          path: menu.path,
          component: menu.component,
          icon: menu.icon,
          menuType: menu.menuType,
          perms: menu.perms,
          visible: menu.visible,
          status: menu.status || 1,
          orderNum: menu.orderNum || 0,
          parentId: menu.parentId || 0,
          children: menu.children ? menu.children.map((child: any) => ({
            id: child.id,
            menuName: child.menuName,
            path: child.path,
            component: child.component,
            icon: child.icon,
            menuType: child.menuType,
            perms: child.perms,
            visible: child.visible,
            status: child.status || 1,
            orderNum: child.orderNum || 0,
            parentId: child.parentId || menu.id,
          })) : undefined,
        }));
        
        // 扁平化处理：将树形结构转换为扁平列表
        const flattenMenus = (items: MenuItem[]): MenuItem[] => {
          const result: MenuItem[] = [];
          items.forEach(item => {
            result.push(item);
            if (item.children && item.children.length > 0) {
              result.push(...flattenMenus(item.children));
            }
          });
          return result;
        };
        
        const flatMenus = flattenMenus(menuItems);
        
        // 简单去重处理
        const uniqueMenus = Array.from(new Map(flatMenus.map((menu: MenuItem) => [menu.id, menu])).values()) as MenuItem[];
        
        // 将菜单数据缓存到当前用户ID或'website'对应的Map中
        globalMenuDataMap.set(cacheKey, uniqueMenus);
        globalIsLoadedMap.set(cacheKey, true);
        
        // 更新 window 对象上的引用
        if (typeof window !== 'undefined') {
          (window as any).__globalMenuDataMap__ = globalMenuDataMap;
          (window as any).__globalIsLoadedMap__ = globalIsLoadedMap;
        }
        
        // 注意：菜单是用户相关的，不应该缓存到localStorage
        // 因为不同用户登录后应该看到不同的菜单
        // saveMenuCache(uniqueMenus); // 注释掉缓存保存
        
        // 更新状态，触发所有使用该 hook 的组件重新渲染
        setMenuData(uniqueMenus);
      } else {
        // 如果接口返回错误，清空菜单数据
        setMenuData([]);
        globalMenuDataMap.delete(cacheKey);
        globalIsLoadedMap.delete(cacheKey);
      }
    } catch (error) {
      // 如果加载失败，清空菜单数据，不显示任何菜单
      // 这样可以确保用户只看到他们有权限的菜单
        console.error('加载菜单失败:', error);
      setMenuData([]);
        if (cacheKey) {
          globalMenuDataMap.delete(cacheKey);
          globalIsLoadedMap.delete(cacheKey);
        }
    } finally {
      setLoading(false);
      globalIsLoading = false;
        loadingPromise = null;
        loadingCacheKey = null;
        isWaitingForLoad = false; // 清除等待标志
        isInitializing = false; // 清除初始化标志
      
      // 更新 window 对象上的引用
      if (typeof window !== 'undefined') {
        (window as any).__globalIsLoading__ = globalIsLoading;
        (window as any).__loadingPromise__ = loadingPromise;
      }
    }
    })();
    
    // 等待 Promise 完成
    await loadingPromise;
  }, []);

  const buildMenuTree = useCallback((menus: MenuItem[]): MenuItem[] => {
    // 使用简化的菜单处理器
    return processSidebarMenuData(menus);
  }, []);

  const convertToMenuItems = useCallback((menus: MenuItem[]): any[] => {
    return menus.map(menu => {
      const item: any = {
        key: menu.id.toString(),
        icon: menu.icon,
        label: menu.menuName,
        path: menu.path,
        children: menu.children ? convertToMenuItems(menu.children) : undefined,
      };
      // 只在需要时添加额外属性，不传递给DOM
      if (menu.menuType) {
        item._menuType = menu.menuType; // 使用下划线前缀，避免传递给DOM
      }
      if (menu.menuName) {
        item._menuName = menu.menuName; // 使用下划线前缀，避免传递给DOM
      }
      if (menu.id) {
        item._id = menu.id; // 使用下划线前缀，避免传递给DOM
      }
      return item;
    });
  }, []);

  useEffect(() => {
    // 检查是否已经有缓存数据，如果有则直接使用，不发起请求
    let currentUserId: number | null = null;
    try {
      const userInfoStr = localStorage.getItem('user_info');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        currentUserId = userInfo?.id || null;
      }
    } catch (e) {
      // 忽略解析错误
    }
    
    const isWebsitePath = window.location.pathname.startsWith('/show');
    let cacheKey: number | string | null = currentUserId;
    if (!cacheKey && isWebsitePath) {
      cacheKey = 'website';
    }
    
    // 如果已经有缓存数据，直接使用，不发起请求
    if (cacheKey) {
      const isLoaded = globalIsLoadedMap.get(cacheKey) || false;
      const cachedData = globalMenuDataMap.get(cacheKey);
      
      if (isLoaded && cachedData && cachedData.length > 0) {
        // 确保状态同步（可能初始化时已经设置，但确保最新）
        setMenuData(cachedData);
        // 如果有缓存数据，不发起新的请求
        return;
      }
    }
    
    // 使用同步检查，确保只有一个组件能通过
    // 如果已经初始化过、正在初始化、正在加载，直接返回
    if (hasInitialized || isInitializing || globalIsLoading || isWaitingForLoad || loadingPromise) {
      // 如果正在加载，等待完成后更新数据
      if (loadingPromise) {
        loadingPromise.then(() => {
          if (cacheKey) {
            const cachedData = globalMenuDataMap.get(cacheKey);
            if (cachedData && cachedData.length > 0) {
              setMenuData(cachedData);
            }
          }
        }).catch((error) => {
          console.error('[useMenuDataSimple] 等待菜单加载失败:', error);
        });
      }
      return;
    }
    
    // 使用同步设置标志，确保立即生效
    // 必须在任何异步操作之前设置，防止其他组件同时通过检查
    // 先设置 hasInitialized，但不设置 isInitializing（让 loadMenuData 内部设置）
    hasInitialized = true;
    isWaitingForLoad = true;
    
    // 直接调用 loadMenuData，不使用 setTimeout
    // 这样可以确保同步执行，避免多个组件同时创建请求
    // isInitializing 将在 loadMenuData 内部设置，确保 loadingPromise 被创建后再设置
    loadMenuData().catch((error) => {
      console.error('菜单加载失败:', error);
      // 如果加载失败，重置标志，允许重试
      hasInitialized = false;
      isWaitingForLoad = false;
      isInitializing = false;
    });
    
    // 监听菜单变更事件
    const handleMenuChanged = () => {
      loadMenuData(true); // 强制重新加载
    };
    
    // 监听登录后的菜单重新加载事件
    const handleMenuReload = (event?: Event) => {
      // 清除所有用户的缓存（因为可能切换了用户）
      globalIsLoadedMap.clear();
      globalMenuDataMap.clear();
      // 清除localStorage缓存
      try {
        localStorage.removeItem(MENU_CACHE_KEY);
      } catch (e) {
        // 忽略
      }
      // 强制重新加载
      loadMenuData(true);
    };
    
    const unsubscribe = eventBus.on('menu:changed', handleMenuChanged);
    
    // 监听window事件（登录成功后触发）
    window.addEventListener('menu:reload', handleMenuReload);
    
    return () => {
      // 注意：不清除 pendingLoadTimer，因为可能有其他组件正在等待
      // 只有在组件真正需要清理时才清理
      unsubscribe();
      window.removeEventListener('menu:reload', handleMenuReload);
    };
  }, []); // 移除 loadMenuData 依赖，使用 useCallback 稳定的函数引用

  // 添加一个 useEffect 监听全局缓存变化，确保所有组件都能及时更新
  // 使用 useRef 防止无限循环
  const menuDataRef = useRef(menuData);
  useEffect(() => {
    menuDataRef.current = menuData;
  }, [menuData]);

  useEffect(() => {
    const checkAndUpdateMenuData = () => {
      let currentUserId: number | null = null;
      try {
        const userInfoStr = localStorage.getItem('user_info');
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);
          currentUserId = userInfo?.id || null;
        }
      } catch (e) {
        // 忽略解析错误
      }
      
      const isWebsitePath = window.location.pathname.startsWith('/show');
      let cacheKey: number | string | null = currentUserId;
      if (!cacheKey && isWebsitePath) {
        cacheKey = 'website';
      }
      
      if (cacheKey) {
        const isLoaded = globalIsLoadedMap.get(cacheKey) || false;
        const cachedData = globalMenuDataMap.get(cacheKey);
        const currentMenuData = menuDataRef.current;
        
        // 如果全局缓存有数据，但本地状态为空，更新本地状态
        if (isLoaded && cachedData && cachedData.length > 0 && currentMenuData.length === 0) {
          setMenuData(cachedData);
        } else if (isLoaded && cachedData && cachedData.length > 0 && currentMenuData.length !== cachedData.length) {
          // 如果全局缓存有数据，但本地状态数量不一致，也更新本地状态
          setMenuData(cachedData);
        }
      }
    };
    
    // 定期检查全局缓存（用于等待中的组件）
    // 只在菜单数据为空时检查，避免不必要的检查
    const intervalId = setInterval(() => {
      if (menuDataRef.current.length === 0) {
        checkAndUpdateMenuData();
      }
    }, 200);
    
    // 立即检查一次
    checkAndUpdateMenuData();
    
    return () => {
      clearInterval(intervalId);
    };
  }, []); // 不依赖 menuData，避免无限循环

  return {
    menuData,
    loading,
    loadMenuData,
    buildMenuTree,
    convertToMenuItems,
  };
};
